/**
 * Decides where to send the user after successful auth.
 *
 * Priority: ?returnTo/?next/?redirect → document.referrer → /portfolio + watchlist modal.
 * Homepage referrers and untrusted/auth-route URLs fall through to the portfolio fallback.
 */

const DEFAULT_TRUSTED_DOMAINS = ['benzinga.com', 'zingbot.bz'] as const;
const PORTFOLIO_PATH = '/profile/portfolio/';
const WATCHLIST_MODAL_QUERY = 'action=create-watchlist';

export interface ResolveReturnUrlInput {
  searchParams?: URLSearchParams | null;
  referrer?: string;
  trustedDomains?: readonly string[];
  currentHost?: string;
  mode?: 'login' | 'register';
}

export interface ResolveReturnUrlResult {
  url: string;
  openWatchlistModal: boolean;
}

/** Strict suffix match — `evil-benzinga.com` does NOT match `benzinga.com`. */
const isTrustedHost = (hostname: string, trustedDomains: readonly string[]): boolean => {
  const lower = hostname.toLowerCase();
  return trustedDomains.some(domain => lower === domain || lower.endsWith(`.${domain}`));
};

const isHomepage = (url: URL): boolean => {
  const path = url.pathname.replace(/\/+$/, '');
  return path === '' || path === '/';
};

/** Auth-loop guard — never redirect back into /login or /register. */
const isAuthRoute = (url: URL): boolean => {
  return /^\/(login|register)(\/|$|\?)/.test(url.pathname + url.search);
};

const parseUrl = (candidate: string, currentHost: string): URL | null => {
  if (!candidate) return null;

  let cleaned: string;
  try {
    cleaned = decodeURIComponent(candidate);
  } catch {
    cleaned = candidate;
  }

  // Strip leading non-alphanumeric junk (matches V1 behavior).
  cleaned = cleaned.replace(/^[^a-zA-Z0-9/]+/, '');
  if (!cleaned) return null;

  try {
    return new URL(cleaned);
  } catch {
    // Fall through to relative.
  }

  try {
    return new URL(cleaned, `https://${currentHost || 'www.benzinga.com'}`);
  } catch {
    return null;
  }
};

const buildPortfolioFallback = (currentHost: string): ResolveReturnUrlResult => {
  // Pin to www regardless of what subdomain the user came from.
  const hostParts = (currentHost || 'www.benzinga.com').split('.');
  const apex = hostParts.slice(-2).join('.') || 'benzinga.com';
  const url = `https://www.${apex}${PORTFOLIO_PATH}?${WATCHLIST_MODAL_QUERY}`;
  return { openWatchlistModal: true, url };
};

export const resolveReturnUrl = ({
  currentHost = '',
  mode = 'login',
  referrer = '',
  searchParams,
  trustedDomains = DEFAULT_TRUSTED_DOMAINS,
}: ResolveReturnUrlInput): ResolveReturnUrlResult => {
  const param =
    searchParams?.get('returnTo') ??
    searchParams?.get('next') ??
    searchParams?.get('redirect') ??
    (mode === 'login' ? searchParams?.get('loginnext') : searchParams?.get('registernext')) ??
    searchParams?.get('registernext') ??
    null;

  if (param) {
    const parsed = parseUrl(param, currentHost);
    if (parsed && isTrustedHost(parsed.hostname, trustedDomains) && !isAuthRoute(parsed)) {
      return { openWatchlistModal: false, url: parsed.toString() };
    }
  }

  if (referrer) {
    const parsed = parseUrl(referrer, currentHost);
    if (parsed && isTrustedHost(parsed.hostname, trustedDomains) && !isAuthRoute(parsed)) {
      // Homepage referrer → portfolio + watchlist modal (better default than reloading the homepage).
      if (isHomepage(parsed)) {
        return buildPortfolioFallback(currentHost || parsed.hostname);
      }
      return { openWatchlistModal: false, url: parsed.toString() };
    }
  }

  return buildPortfolioFallback(currentHost);
};
